/**
 * Nomination Approval Service
 * 
 * Service layer for managing nomination approval related API operations.
 * This service handles fetching nomination approval data for managers.
 * 
 * API Endpoints:
 * - GET /api/v1/feedback/manager_nomination_review_list/?email={email} - Get list of nominations for approval
 * - POST /api/v1/feedback/approve_nomination/ - Approve a nomination
 * - POST /api/v1/feedback/reject_nomination/ - Reject a nomination with reason
 * 
 * Note: API requires Authorization header with Token authentication
 * Uses Vite proxy configuration to route requests to staging server
 */

import { apiService, type ApiResponse } from './api';

/**
 * Interface for nomination approval list item from API
 */
export interface NominationApprovalItem {
  emp_id: string | null;
  employee_name: string | null;
  designation: string | null;
  department: string | null;
  manager_name: string | null;
  status: 'Approved' | 'Pending' | 'Rejected' | null;
  nomination_id: number | null;
  emp_email?: string | null; // Updated to match API response field name
  manager_email?: string | null;
  okr_status?: 'draft' | 'submitted' | string | null; // OKR submission status
  okr_id?: number | null; // OKR ID reference
}

/**
 * Interface for bulk action request
 */
export interface BulkActionRequest {
  manager_email: string;
  comment: string;
  action: 'approved' | 'rejected';
  action_list: Array<{
    nomination_id: number;
    nomination_employee_email: string;
  }>;
}

/**
 * Interface for bulk action response
 */
export interface BulkActionResponse {
  status: boolean;
  message: string;
  data: {
    updated: Array<{
      nomination_id: number;
      status: string;
    }>;
    failed: Array<{
      nomination_id: number;
      error: string;
    }>;
  };
  error: string | null;
  status_code: number;
}

/**
 * Interface for nomination detail response
 */
export interface NominationDetailResponse {
  nomination_id: number;
  status: string;
  comments: string;
  employee: {
    email: string;
    full_name: string;
    department_name: string;
    manager_name: string;
    designation: string;
    employee_id: string;
    is_active: boolean;
  };
  nominations: {
    cycle_id: number;
    nomination_manager: Array<{
      id: number;
      emp_id: string;
      email: string;
      full_name: string;
    }>;
    superiors: Array<{
      id: number;
      emp_id: string;
      email: string;
      full_name: string;
    }>;
    dotted_superiors?: Array<{
      id: number;
      emp_id: string | null;
      email: string;
      full_name: string;
      external?: boolean;
    }>;
    subordinates: Array<{
      id: number;
      emp_id: string;
      email: string;
      full_name: string;
    }>;
    additional_subordinate?: Array<{
      id: number;
      emp_id: string | null;
      email: string;
      full_name: string;
      external?: boolean;
    }>;
    additional_subordinates?: Array<{
      id: number;
      emp_id: string | null;
      email: string;
      full_name: string;
      external?: boolean;
    }>;
    peers: Array<{
      id: number;
      emp_id: string;
      email: string;
      full_name: string;
    }>;
    stakeholders: Array<{
      id: number;
      emp_id: string;
      email: string;
      full_name: string;
    }>;
    nomination_end_date: string;
    nomination_cycle_left: number;
  };
}

/**
 * Interface for nomination action response
 */
export interface NominationActionResponse {
  status: boolean;
  message: string;
  data?: {
    nomination_id: number;
    status: string;
    comments: string;
  };
  error: string | null;
  status_code: number;
}

/**
 * API Response interface for nomination approval list
 */
export interface NominationApprovalListResponse {
  status: boolean;
  message: string;
  data: {
    nomination_review_list: NominationApprovalItem[];
  };
  error: string | null;
  status_code: number;
}

/**
 * Nomination Approval Service class
 * Handles all nomination approval related API operations
 */
class NominationApprovalService {

  /**
   * Get nomination approval list for a manager
   * 
   * @param email - Manager's email address
   * @returns Promise<ApiResponse<NominationApprovalListResponse>>
   */
  async getNominationApprovalList(email: string): Promise<ApiResponse<NominationApprovalListResponse>> {
    try {
      // Use relative URL to work with Vite proxy configuration
      const url = `/api/v1/feedback/manager_nomination_review_list/?email=${encodeURIComponent(email)}`;
      
      console.log('🔄 Fetching nomination approval list for:', email);
      console.log('🔄 API URL:', url);
      console.log('🔄 This will be proxied to the staging server via Vite');

      const response = await apiService.get<NominationApprovalListResponse>(url);
      
      console.log('✅ Nomination approval list response:', response);
      
      return response;
    } catch (error) {
      console.error('❌ Error fetching nomination approval list:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error
      });
      
      // Return error response in consistent format
      return {
        status: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch nomination approval list',
        message: 'Failed to fetch nomination approval list',
        status_code: 500
      };
    }
  }

  /**
   * Transform API data to component-friendly format
   * 
   * @param apiData - Raw API response data
   * @returns Transformed data for the component
   */
  transformApiData(apiData: NominationApprovalItem[]): Array<{
    id: string;
    employeeId: string;
    employeeName: string;
    designation: string;
    department: string;
    managerName: string;
    nominationStatus: 'Approved' | 'Pending' | 'Rejected';
    employeeEmail: string;
    managerEmail?: string;
    okrStatus?: string;
    okrId?: number;
    nominationId?: number | null; // Add nomination_id to track if it exists
  }> {
    return apiData.map((item) => ({
      // Use nomination_id if available, otherwise use okr_id as identifier
      // This allows navigation when only OKR data exists
      id: item.nomination_id?.toString() || item.okr_id?.toString() || '',
      employeeId: item.emp_id || '',
      employeeName: item.employee_name || '',
      designation: item.designation || '',
      department: item.department || '',
      managerName: item.manager_name || '',
      nominationStatus: item.status || 'Pending',
      employeeEmail: item.emp_email || '',
      managerEmail: item.manager_email || '',
      okrStatus: item.okr_status || '',
      okrId: item.okr_id || undefined,
      nominationId: item.nomination_id // Store original nomination_id to check if it's null
    }));
  }

  /**
   * Approve a nomination
   * 
   * @param nominationId - ID of the nomination to approve
   * @param email - Manager's email address
   * @returns Promise<ApiResponse<NominationActionResponse>>
   */
  async approveNomination(nominationId: number, email: string): Promise<ApiResponse<NominationActionResponse>> {
    try {
      const url = `/api/v1/feedback/approve_nomination/`;
      const payload = {
        nomination_id: nominationId,
        email: email
      };
      
      console.log('🔄 Approving nomination:', payload);

      const response = await apiService.post<NominationActionResponse>(url, payload);
      
      console.log('✅ Nomination approved:', response);
      
      return response;
    } catch (error) {
      console.error('❌ Error approving nomination:', error);
      
      // Re-throw the error to preserve the original API response
      throw error;
    }
  }

  /**
   * Get detailed nomination data for a specific employee
   * 
   * @param employeeEmail - Employee's email address
   * @returns Promise<ApiResponse<NominationDetailResponse>>
   */
  async getNominationDetail(employeeEmail: string): Promise<ApiResponse<NominationDetailResponse>> {
    try {
      const url = `/api/v1/feedback/nomination/?email=${encodeURIComponent(employeeEmail)}`;
      
      console.log('🔄 Fetching detailed nomination data for employee:', employeeEmail);
      console.log('🔄 API URL:', url);

      const response = await apiService.get<NominationDetailResponse>(url);
      
      console.log('✅ Nomination detail response:', response);
      
      return response;
    } catch (error) {
      console.error('❌ Error fetching nomination detail:', error);
      
      // Re-throw the error to preserve the original API response
      throw error;
    }
  }

  /**
   * Get detailed nomination data for approval review
   * Only fetches when nomination ID is provided (not null)
   * 
   * @param identifier - Nomination ID (required, must not be null)
   * @param managerEmail - Manager's email address
   * @returns Promise<ApiResponse<NominationDetailResponse>>
   */
  async getNominationApprovalDetail(identifier: string, managerEmail: string): Promise<ApiResponse<NominationDetailResponse>> {
    try {
      // Check if identifier is null, undefined, or empty string
      if (!identifier || identifier.trim() === '') {
        console.warn('⚠️ Nomination ID is null or empty, skipping API call');
        return {
          status: false,
          data: null,
          error: 'Nomination ID is required',
          message: 'Cannot fetch nomination details without a valid nomination ID',
          status_code: 400
        };
      }

      // Only use nomination_id (no email support)
      const url = `/api/v1/feedback/manager_nomination_review/?nomination_id=${identifier}&manager_email=${encodeURIComponent(managerEmail)}`;
      console.log('🔄 Fetching nomination detail by nomination ID:', { nominationId: identifier, managerEmail });

      const response = await apiService.get<NominationDetailResponse>(url);
      
      console.log('✅ Nomination detail response:', response);
      
      return response;
    } catch (error) {
      console.error('❌ Error fetching nomination detail:', error);
      
      // Re-throw the error to preserve the original API response
      throw error;
    }
  }

  /**
   * Update nomination action (approve or reject)
   * 
   * @param nominationId - ID of the nomination
   * @param managerEmail - Manager's email address
   * @param action - Action to take ('approved' or 'rejected')
   * @param comments - Comments from manager
   * @returns Promise<ApiResponse<NominationActionResponse>>
   */
  async updateNominationAction(
    nominationId: string, 
    managerEmail: string, 
    action: 'approved' | 'rejected', 
    comments: string
  ): Promise<ApiResponse<NominationActionResponse>> {
    try {
      const url = `/api/v1/feedback/manager_nomination_action/`;
      const payload = {
        nomination_id: parseInt(nominationId),
        manager_email: managerEmail,
        action: action,
        comments: comments
      };
      
      console.log('🔄 Updating nomination action:', payload);

      const response = await apiService.put<NominationActionResponse>(url, payload);
      
      console.log('✅ Nomination action updated:', response);
      
      return response;
    } catch (error) {
      console.error('❌ Error updating nomination action:', error);
      
      // Re-throw the error to preserve the original API response
      throw error;
    }
  }

  /**
   * Perform bulk nomination action (approve or reject multiple nominations)
   * 
   * @param managerEmail - Manager's email address
   * @param action - Action to perform ('approved' or 'rejected')
   * @param comment - Comment for the action
   * @param nominations - Array of nominations with their details
   * @returns Promise<ApiResponse<{ updated: Array<{ nomination_id: number; status: string }>; failed: Array<{ nomination_id: number; error: string }> }>>
   */
  async performBulkAction(
    managerEmail: string,
    action: 'approved' | 'rejected',
    comment: string,
    nominations: Array<{ id: string; employeeEmail: string }>
  ): Promise<ApiResponse<{ updated: Array<{ nomination_id: number; status: string }>; failed: Array<{ nomination_id: number; error: string }> }>> {
    try {
      const url = `/api/v1/feedback/manager_nomination_bulk_action/`;
      const payload: BulkActionRequest = {
        manager_email: managerEmail,
        comment: comment,
        action: action,
        action_list: nominations.map(nomination => ({
          nomination_id: parseInt(nomination.id),
          nomination_employee_email: nomination.employeeEmail
        }))
      };
      
      console.log('🔄 Performing bulk nomination action:', {
        url,
        action,
        nominationCount: nominations.length,
        payload
      });

      const response = await apiService.put<{ updated: Array<{ nomination_id: number; status: string }>; failed: Array<{ nomination_id: number; error: string }> }>(url, payload);
      
      console.log('✅ Bulk nomination action completed:', {
        status: response.status,
        message: response.message,
        data: response.data
      });
      
      return response;
    } catch (error) {
      console.error('❌ Error performing bulk nomination action:', {
        error,
        action,
        nominationCount: nominations.length,
        managerEmail
      });
      
      // Re-throw the error to preserve the original API response
      throw error;
    }
  }

  /**
   * Reject a nomination
   * 
   * @param nominationId - ID of the nomination to reject
   * @param email - Manager's email address
   * @param reason - Reason for rejection
   * @returns Promise<ApiResponse<NominationActionResponse>>
   */
  async rejectNomination(nominationId: number, email: string, reason: string): Promise<ApiResponse<NominationActionResponse>> {
    try {
      const url = `/api/v1/feedback/reject_nomination/`;
      const payload = {
        nomination_id: nominationId,
        email: email,
        rejection_reason: reason
      };
      
      console.log('🔄 Rejecting nomination:', payload);

      const response = await apiService.post<NominationActionResponse>(url, payload);
      
      console.log('✅ Nomination rejected:', response);
      
      return response;
    } catch (error) {
      console.error('❌ Error rejecting nomination:', error);
      
      // Re-throw the error to preserve the original API response
      throw error;
    }
  }
}

// Export singleton instance
export const nominationApprovalService = new NominationApprovalService();
export default nominationApprovalService;
